import { isPunctuationMark } from '../utils'

export interface TextSelection {
  rangeBoundaries: DOMRect
  selection: Selection | null
  range: Range
  speaker: string
}

export const processTextSelection = (
  isAllowed = true
): TextSelection | null => {
  let selection: Selection | null, range: Range, rangeBoundaries: DOMRect

  const correctBackwardSelectionStart = (selection: Selection | null) => {
    if (selection && selection.focusNode && selection.focusNode.parentElement) {
      if (
        !selection.focusNode.textContent?.trim().length &&
        selection.focusNode.parentElement.nextElementSibling
      ) {
        // If last selected character is ' ' (space) - start selection from the next word
        range.setStartBefore(
          selection.focusNode.parentElement.nextElementSibling.childNodes[0]
        )
      } else if (
        isPunctuationMark(`${selection.focusNode.textContent}`) &&
        selection.focusNode.parentElement.previousElementSibling
      ) {
        // If last selected character is a punctuation symbol - select the word which goes before it
        range.setStartBefore(
          selection.focusNode.parentElement.previousElementSibling.childNodes[0]
        )
      } else {
        range.setStartBefore(selection.focusNode)
      }
    }
  }

  const correctSelectionEnd = (
    selection: Selection | null,
    direction: string
  ): void => {
    let node
    if (selection) {
      if (direction === 'backward') {
        node = selection.anchorNode
      } else {
        node = selection.focusNode
      }
    }

    if (node && node.parentElement) {
      if (
        !node.textContent?.trim().length &&
        node.parentElement.previousElementSibling
      ) {
        // If last selected character is ' ' (space) - end selection on the previous word
        range.setEndAfter(
          node.parentElement.previousElementSibling.childNodes[0]
        )
      } else if (
        node.parentElement.nextElementSibling &&
        isPunctuationMark(
          `${node.parentElement.nextElementSibling.textContent?.trim()}`
        )
      ) {
        // If the next element to the selection is a punctuation symbol - select it too
        range.setEndAfter(node.parentElement.nextElementSibling.childNodes[0])
      } else {
        range.setEndAfter(node)
      }
    }
  }

  const correctForwardSelectionStart = (selection: Selection | null): void => {
    if (selection && selection.anchorNode) {
      if (
        !selection.anchorNode.textContent?.trim().length &&
        !!selection.anchorNode.parentElement &&
        !!selection.anchorNode.parentElement.nextElementSibling
      ) {
        // If first selected character is ' ' (space) - start selection from the next word
        range.setStartBefore(
          selection.anchorNode.parentElement.nextElementSibling.childNodes[0]
        )
      } else if (
        isPunctuationMark(`${selection.anchorNode.textContent?.trim()}`) &&
        selection.anchorNode.parentElement &&
        selection.anchorNode.parentElement.previousElementSibling
      ) {
        // If first selected character is a punctuation symbol - select a word before it too
        range.setStartBefore(
          selection.anchorNode.parentElement.previousElementSibling
            .childNodes[0]
        )
      } else {
        range.setStartBefore(selection.anchorNode)
      }
    }
  }

  if (window.getSelection) {
    selection = window.getSelection()

    if (selection !== undefined && selection !== null) {
      // Define if the direction of selection is backward

      // if (!isAllowed) {
      //     selection.removeAllRanges()
      //     return null
      // }

      if (
        selection.anchorNode &&
        selection.focusNode &&
        selection.toString().length
      ) {
        // handle single  word in editor
        if (
          selection.anchorNode.parentElement &&
          (selection.anchorNode
            .parentElement as HTMLElement).className.includes('single')
        ) {
          const range = selection.getRangeAt(0)
          const rangeBoundaries = range.getBoundingClientRect()
          selection.removeAllRanges()
          selection.addRange(range)
          const speaker = (range.startContainer as HTMLElement).getAttribute(
            'speaker-name'
          )
          return {
            selection,
            range,
            rangeBoundaries,
            speaker
          }
        }

        const position = selection.anchorNode.compareDocumentPosition(
          selection.focusNode
        )
        const isBackwardDirection =
          (!position && selection.anchorOffset > selection.focusOffset) ||
          position === Node.DOCUMENT_POSITION_PRECEDING
        range = new Range()

        if (isBackwardDirection) {
          // This part of code does not allow to select not an entire word by selecting the rest of words
          correctBackwardSelectionStart(selection)
          correctSelectionEnd(selection, 'backward')
        } else {
          correctForwardSelectionStart(selection)
          correctSelectionEnd(selection, 'forward')
        }

        rangeBoundaries = range.getBoundingClientRect()
        selection.removeAllRanges()
        selection.addRange(range)

        if (
          !(range.startContainer as HTMLElement).className.includes('word') ||
          !(range.endContainer as HTMLElement).className.includes('word')
        ) {
          return null
        }
        const speaker = (range.startContainer as HTMLElement).getAttribute(
          'speaker-name'
        )
        return {
          rangeBoundaries,
          selection,
          range,
          speaker
        }
      }
    }
  }

  return null
}
